import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { stepAddsOneOf } from '@atlaskit/editor-common/utils';
import { getChangedNodes, isReplaceDocOperation } from '@atlaskit/editor-common/utils/document';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { akEditorDefaultLayoutWidth, akEditorFullWidthLayoutWidth, akEditorCalculatedWideLayoutWidth } from '@atlaskit/editor-shared-styles';
import { fg } from '@atlaskit/platform-feature-flags';
import { handleKeyDown } from './handle-key-down';
import { ResizingMarkView } from './resizing-mark-view';
import { updateExpandedStateNew } from './utils/single-player-expand';
var addBreakoutToResizableNode = function addBreakoutToResizableNode(_ref) {
  var node = _ref.node,
    pos = _ref.pos,
    newState = _ref.newState,
    newTr = _ref.newTr,
    breakoutResizableNodes = _ref.breakoutResizableNodes,
    isFullWidthEnabled = _ref.isFullWidthEnabled;
  var updatedDocChanged = false;
  var updatedTr = newTr;
  var $pos = newState.doc.resolve(pos);
  var isTopLevelNode = ($pos === null || $pos === void 0 ? void 0 : $pos.depth) === 0;
  if (breakoutResizableNodes.has(node.type) && isTopLevelNode) {
    var breakout = newState.schema.marks.breakout;
    var expand = newState.schema.nodes.expand;
    var breakoutMark = node.marks.find(function (mark) {
      return mark.type === breakout;
    });
    var isExpand = node.type === expand;
    if (!breakoutMark) {
      var width = isFullWidthEnabled ? akEditorFullWidthLayoutWidth : akEditorDefaultLayoutWidth;
      updatedTr = newTr.setNodeMarkup(pos, node.type, node.attrs, [breakout.create({
        width: width
      })]);
      if (isExpand) {
        updateExpandedStateNew({
          tr: updatedTr,
          node: node,
          pos: pos,
          isLivePage: true
        });
      }
      updatedDocChanged = true;
    } else if ((breakoutMark === null || breakoutMark === void 0 ? void 0 : breakoutMark.attrs.width) === null || (breakoutMark === null || breakoutMark === void 0 ? void 0 : breakoutMark.attrs.width) === undefined) {
      var mode = breakoutMark.attrs.mode;
      var newWidth = fg('platform_editor_breakout_resizing_patch_1') ?
      // if breakout is present on node, but page appearance is 'full width' force width to full width to maintain backwards compatibility
      isFullWidthEnabled || mode === 'full-width' ? akEditorFullWidthLayoutWidth : akEditorCalculatedWideLayoutWidth : mode === 'wide' ? akEditorCalculatedWideLayoutWidth : akEditorFullWidthLayoutWidth;
      updatedTr = newTr.setNodeMarkup(pos, node.type, node.attrs, [breakout.create({
        width: newWidth,
        mode: mode
      })]);
      if (isExpand) {
        updateExpandedStateNew({
          tr: updatedTr,
          node: node,
          pos: pos,
          isLivePage: true
        });
      }
      updatedDocChanged = true;
    }
  }
  return {
    updatedTr: updatedTr,
    updatedDocChanged: updatedDocChanged
  };
};
export var resizingPluginKey = new PluginKey('breakout-resizing');
var pluginState = {
  init: function init() {
    return {
      breakoutNode: undefined,
      activeGuidelineLabel: undefined
    };
  },
  apply: function apply(tr, pluginState) {
    var meta = tr.getMeta(resizingPluginKey);
    if (meta) {
      switch (meta.type) {
        case 'UPDATE_BREAKOUT_NODE':
          return _objectSpread(_objectSpread({}, pluginState), {}, {
            breakoutNode: meta.data
          });
        case 'UPDATE_ACTIVE_GUIDELINE_KEY':
          return _objectSpread(_objectSpread({}, pluginState), {}, {
            activeGuidelineKey: meta.data.activeGuidelineKey
          });
        case 'CLEAR_ACTIVE_GUIDELINE_KEY':
          return _objectSpread(_objectSpread({}, pluginState), {}, {
            activeGuidelineKey: undefined
          });
        case 'RESET_STATE':
          return {
            activeGuidelineLabel: undefined,
            breakoutNode: undefined
          };
      }
    }
    return pluginState;
  }
};
export var createResizingPlugin = function createResizingPlugin(api, getIntl, nodeViewPortalProviderAPI, options) {
  return new SafePlugin({
    key: resizingPluginKey,
    state: pluginState,
    props: {
      markViews: {
        breakout: function breakout(mark, view) {
          return new ResizingMarkView(mark, view, api, getIntl, nodeViewPortalProviderAPI);
        }
      },
      handleKeyDown: handleKeyDown(api)
    },
    appendTransaction: function appendTransaction(transactions, oldState, newState) {
      if (fg('platform_editor_breakout_resizing_patch_1')) {
        var _api$editorViewMode;
        if ((api === null || api === void 0 || (_api$editorViewMode = api.editorViewMode) === null || _api$editorViewMode === void 0 || (_api$editorViewMode = _api$editorViewMode.sharedState.currentState()) === null || _api$editorViewMode === void 0 ? void 0 : _api$editorViewMode.mode) === 'view') {
          return;
        }
      } else {
        var _api$editorViewMode2;
        // if editor is in live-view mode don't send transactions
        if ((api === null || api === void 0 || (_api$editorViewMode2 = api.editorViewMode) === null || _api$editorViewMode2 === void 0 || (_api$editorViewMode2 = _api$editorViewMode2.sharedState.currentState()) === null || _api$editorViewMode2 === void 0 ? void 0 : _api$editorViewMode2.mode) !== 'edit') {
          return;
        }
      }
      var newTr = newState.tr;
      var hasDocChanged = false;
      var _newState$schema$node = newState.schema.nodes,
        expand = _newState$schema$node.expand,
        codeBlock = _newState$schema$node.codeBlock,
        layoutSection = _newState$schema$node.layoutSection;
      var breakoutResizableNodes = new Set([expand, codeBlock, layoutSection]);
      var isFullWidthEnabled = !((options === null || options === void 0 ? void 0 : options.allowBreakoutButton) === true);
      if (isReplaceDocOperation(transactions, oldState)) {
        newState.doc.forEach(function (node, pos) {
          var _addBreakoutToResizab = addBreakoutToResizableNode({
              node: node,
              pos: pos,
              newState: newState,
              newTr: newTr,
              breakoutResizableNodes: breakoutResizableNodes,
              isFullWidthEnabled: isFullWidthEnabled
            }),
            updatedTr = _addBreakoutToResizab.updatedTr,
            updatedDocChanged = _addBreakoutToResizab.updatedDocChanged;
          newTr = updatedTr;
          hasDocChanged = hasDocChanged || updatedDocChanged;
        });
      } else {
        transactions.forEach(function (tr) {
          var isAddingResizableNodes = tr.steps.some(function (step) {
            return stepAddsOneOf(step, breakoutResizableNodes);
          });
          if (isAddingResizableNodes) {
            var changedNodes = getChangedNodes(tr);
            changedNodes.forEach(function (_ref2) {
              var node = _ref2.node,
                pos = _ref2.pos;
              var _addBreakoutToResizab2 = addBreakoutToResizableNode({
                  node: node,
                  pos: pos,
                  newState: newState,
                  newTr: newTr,
                  breakoutResizableNodes: breakoutResizableNodes,
                  isFullWidthEnabled: isFullWidthEnabled
                }),
                updatedTr = _addBreakoutToResizab2.updatedTr,
                updatedDocChanged = _addBreakoutToResizab2.updatedDocChanged;
              newTr = updatedTr;
              hasDocChanged = hasDocChanged || updatedDocChanged;
            });
          }
        });
      }
      if (hasDocChanged) {
        return newTr;
      }
    }
  });
};